import { Component, EventEmitter, OnDestroy } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialogRef } from '@angular/material/dialog';
import { PortalModuleBlueprint } from '@src/app/model/portal-module-blueprint';
import { takeUntil } from 'rxjs';
import { first } from 'rxjs/operators';
import { Archived, draftStatus, publishedStatus } from '@ess/jg-rule-executor';

@Component({
  selector: 'app-new-blueprint',
  templateUrl: './new-blueprint.component.html',
  styleUrls: ['./new-blueprint.component.scss'],
  standalone: false,
})
export class NewBlueprintComponent implements OnDestroy {
  public currentName: string = '';
  public disabled: boolean = false;
  public blueprintHasDraft: boolean = false;
  public blueprintExists: boolean = false;

  private _latestBlueprints?: PortalModuleBlueprint[];

  public result: EventEmitter<{ blueprint?: PortalModuleBlueprint, name: string, new: boolean }>
    = new EventEmitter<{ blueprint: PortalModuleBlueprint, name: string, new: boolean }>();

  private _componentDestroyed$: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private _apolloService: ApolloService,
    private _dialogRef: MatDialogRef<NewBlueprintComponent>,
  ) {
  }

  public validateName(): void {
    this.currentName = this.currentName.trim(); // remove spaces at beginning / end
    this.disabled = true;
    this._apolloService.getBlueprintsByName$(this.currentName).pipe(
      takeUntil(this._componentDestroyed$),
      first()
    ).subscribe({
      next: (r) => {
        // Save results
        this._latestBlueprints = r;
        this.blueprintHasDraft = r.filter(t => t.status.__typename === draftStatus.__typename).length > 0;
        this.blueprintExists = r.length > 0;
        // If no blueprint exists, just go make one
        if (!this.blueprintExists) {
          this.result.next({ name: this.currentName, new: true });
          this._dialogRef.close();
        }
        // Otherwise, the UI handles the rest
      },
    });
  }

  public copy() {
    // Determine correct blueprint to copy (the published one, and otherwise the latest)
    const blueprintToCopy = this._latestBlueprints.find(t => t.status.__typename === publishedStatus.__typename)
      ?? this._latestBlueprints.sort((a, b) => (b.status as Archived).version - (a.status as Archived).version)[0];
    this.result.next({ name: this.currentName, blueprint: blueprintToCopy, new: true });
    this._dialogRef.close();
  }

  public retry() {
    this.currentName = '';
    this.disabled = false;
    this.blueprintExists = false;
    this.blueprintHasDraft = false;
    this._latestBlueprints = undefined;
  }

  public edit() {
    const draftBlueprint = this._latestBlueprints.find(t => t.status.__typename === draftStatus.__typename);
    this.result.next({ name: this.currentName, blueprint: draftBlueprint, new: false });
    this._dialogRef.close();
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
  }
}
